import { wsTopic } from '@/data/websocketTopics'
import { serializeCase, serializeMessage } from '@/modules/Cases/serializers'
import type { Case, MessageResponse } from '@/modules/Cases/types'
import { useWebSocketTopicArray } from '@/sharedComposables/useWebSocketTopicArray'
import { useQueryClient } from '@tanstack/vue-query'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

/**
 * Private store to be used in `useCaseQuery()` only.
 * Manages WebSocket subscriptions for case updates using ref counting.
 */
export const useCaseSubscriptionStore = defineStore('caseSubscription', () => {
  const refCounts = ref<Map<string, { count: number; workspaceId: string }>>(new Map())
  const client = useQueryClient()

  // All topics with >0 subscribers
  const activeTopics = computed(() =>
    Array.from(refCounts.value.entries())
      .filter(([_, { count }]) => count > 0)
      .map(([caseId, { workspaceId }]) => wsTopic.case(workspaceId, caseId))
      .filter((topic): topic is NonNullable<typeof topic> => topic !== null),
  )

  const { subscribe } = useWebSocketTopicArray(activeTopics)

  subscribe('case:updated', (data) => {
    const caseId = data.case.id
    const refCountEntry = refCounts.value.get(caseId)
    if (refCountEntry) {
      client.setQueryData(getQueryKey(caseId), serializeCase(data.case, refCountEntry.workspaceId))
    }
  })

  subscribe('case:message_stream', ({ message }: { message: MessageResponse }, topic) => {
    // Extract case ID from topic: "case:workspaceId:caseId"
    const topicParts = topic.split(':')
    const caseId = topicParts[2]

    if (!caseId) return

    const serializedMessage = serializeMessage(message)
    client.setQueryData(getQueryKey(caseId), (oldData: Case | undefined) => {
      if (!oldData) return

      const newCase = structuredClone(oldData)
      const existingMessageIndex = oldData.messages.findIndex((m) => m.id === serializedMessage.id)

      if (existingMessageIndex >= 0) {
        newCase.messages[existingMessageIndex] = serializedMessage
      } else {
        newCase.messages.push(serializedMessage)
      }

      return newCase
    })
  })

  const addInterest = (caseId: string, workspaceId: string) => {
    const current = refCounts.value.get(caseId)
    if (current) {
      refCounts.value.set(caseId, { ...current, count: current.count + 1 })
    } else {
      refCounts.value.set(caseId, { count: 1, workspaceId })
    }
  }

  const removeInterest = (caseId: string) => {
    const current = refCounts.value.get(caseId)
    if (!current) return

    if (current.count <= 1) {
      refCounts.value.delete(caseId)
    } else {
      refCounts.value.set(caseId, { ...current, count: current.count - 1 })
    }
  }

  return { addInterest, removeInterest, refCounts, activeTopics }
})

export function getQueryKey(caseId: string | null | undefined) {
  return ['case', caseId]
}
